import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router';

import type { Role } from '@/shared/api/types';
import { homeUrl, interfaceOf } from '@/shared/lib/roles';
import { browser, isElsewhere, isHere, isOwnUrl, urlOf } from '@/shared/lib/site';
import { Spinner } from '@/shared/ui/primitives';

/**
 * Where an account goes once it is signed in: back to the page that asked for the
 * sign-in, if that page is this installation's, or to where its role starts.
 * Anything else that was asked for is somebody's attempt to send people elsewhere
 * through the sign-in form, and is ignored.
 */
export function afterSignIn(role: Role, asked?: string | null): string {
  return asked && isOwnUrl(asked) ? asked : homeUrl(interfaceOf(role));
}

/**
 * The way in, from a page that needs an account. There is one sign-in form, at the
 * front of the installation; a page of another host says where to come back to.
 */
export function signInUrl(from: { pathname: string; search: string }): string {
  if (isHere('home')) return '/login';
  const back = `${globalThis.location.origin}${from.pathname}${from.search}`;
  return `${urlOf('home', '/login')}?next=${encodeURIComponent(back)}`;
}

/** Go to a page: by the router when it is here, by the browser when it is on another host. */
export function GoTo({ to, state }: { to: string; state?: unknown }) {
  const { t } = useTranslation();
  const elsewhere = isElsewhere(to);
  useEffect(() => {
    if (elsewhere) browser.leave(to);
  }, [elsewhere, to]);
  if (elsewhere) return <Spinner label={t('loading')} />;
  return <Navigate to={to} replace state={state} />;
}
